/**
 * Copying a week and applying a template (issue #77, moved out of Vaktplan.tsx): one review drawer
 * whose state moves through a preview, a send and, when an answer is lost, a status check. The
 * page runs the requests; this reducer is every state change they cause, so it is tested without
 * mounting the page (copyMachine.test.ts).
 *
 * Both go through one server route that reads the source itself, maps every shift onto the
 * destination in the company's own clock and says what it would create before anything is written
 * (TF-SCHEDULE-COMPLETE). The confirm asks for exactly the same thing with a key, so a lost answer
 * can be asked again without making a second set of drafts.
 */
import type { ApiPlan, ApplyTemplateBody, CopyWindowBody } from '@/api/data';
import { T } from '@/i18n';

/** What the review drawer is about: a week copy or a template, with the request key it will use. */
export interface PlanState {
  kind: 'copy' | 'template';
  title: string;
  templateId?: number;
  copy?: CopyWindowBody;
  apply?: ApplyTemplateBody;
  plan: ApiPlan | null;
  busy: boolean;
  /** an answer never arrived: nothing may be sent again until a recheck settles it */
  unknown: boolean;
  /** a status check proved nothing was created, so creating is offered again, on its own */
  confirmed_absent?: boolean;
  problem: string;
  info: string;
}

/** A key for one reviewed request, so asking again after a lost answer creates nothing new. */
export const newKey = () =>
  globalThis.crypto?.randomUUID?.() ?? `k${Date.now()}-${Math.random().toString(36).slice(2)}`;
/** What a request is, without its own identity: the same work asked for twice keeps one key. */
export const signatureOf = (p: PlanState) =>
  JSON.stringify([p.kind, p.templateId ?? null, p.copy ?? null, p.apply ?? null]);

export interface CopyState {
  /** the open review drawer, or null */
  plan: PlanState | null;
  /**
   * Which pieces of work (by signature) have an outcome nobody knows. Kept across the drawer
   * closing: reopening the same copy finds it still unknown (acceptance review, finding 1).
   */
  unknownWork: Set<string>;
}
export const idle: CopyState = { plan: null, unknownWork: new Set() };

export type CopyEvent =
  /** ask the server what the work would create; a piece of work with an unknown outcome stays so */
  | { type: 'preview'; next: PlanState }
  | { type: 'previewed'; plan: ApiPlan }
  /** a send or a status check is under way */
  | { type: 'sending' }
  /** the server refused, or the check failed: said in the drawer, nothing else changes */
  | { type: 'problem'; text: string }
  | { type: 'info'; text: string }
  | { type: 'close' }
  /** the work was done, now or by an earlier request whose answer was lost */
  | { type: 'done'; sig: string }
  /** a status check proved the request never arrived: creating is offered again, on its own */
  | { type: 'absent'; sig: string }
  /** nobody knows whether the send created anything */
  | { type: 'unknown'; sig: string };

export const UNKNOWN_COPY = 'Vi vet ikke om kopien ble laget. Sjekk status før du prøver igjen.';

const without = (s: Set<string>, sig: string) => {
  const n = new Set(s);
  n.delete(sig);
  return n;
};

export function copyMachine(state: CopyState, e: CopyEvent): CopyState {
  const p = state.plan;
  switch (e.type) {
    case 'preview': {
      const unknown = state.unknownWork.has(signatureOf(e.next));
      return {
        ...state,
        plan: {
          ...e.next,
          plan: null,
          busy: true,
          problem: unknown ? T(UNKNOWN_COPY) : '',
          info: '',
          unknown,
        },
      };
    }
    case 'previewed':
      return p ? { ...state, plan: { ...p, plan: e.plan, busy: false } } : state;
    case 'sending':
      return p ? { ...state, plan: { ...p, busy: true, problem: '', info: '' } } : state;
    case 'problem':
      return p ? { ...state, plan: { ...p, busy: false, problem: e.text } } : state;
    case 'info':
      return p ? { ...state, plan: { ...p, busy: false, info: e.text } } : state;
    case 'close':
      return { ...state, plan: null };
    case 'done':
      return { plan: null, unknownWork: without(state.unknownWork, e.sig) };
    case 'absent':
      return {
        unknownWork: without(state.unknownWork, e.sig),
        plan: p
          ? {
              ...p,
              busy: false,
              unknown: false,
              confirmed_absent: true,
              info: T('Ingenting ble laget. Du kan lage kopien nå.'),
            }
          : p,
      };
    case 'unknown':
      return {
        unknownWork: new Set(state.unknownWork).add(e.sig),
        plan: p ? { ...p, busy: false, unknown: true, confirmed_absent: false, problem: T(UNKNOWN_COPY) } : p,
      };
  }
}
